import { useEffect, useMemo, useRef, useState } from "react";
import { Head, router, useForm } from "@inertiajs/react";
import { diffLines } from "diff";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import AuthLayout, { useAuthLayoutActions, useAuthLayoutTitle } from "@/layouts/auth-layout";
import RichTextEditor, { normalizeRichText } from "@/components/markdown-editor";
import ContentLibraryPdfPreview from "@/components/content-library-pdf-preview";
import { document_path, documents_path, edit_guide_path, edit_template_path, guide_path, guides_path, new_document_path, new_guide_path, new_template_path, template_path, templates_path } from "@/routes";

const configs = {
  template: { pluralLabel: "Skabeloner", singularLabel: "Skabelon", collectionPath: templates_path, newPath: new_template_path, updatePath: template_path },
  document: { pluralLabel: "Dokumenter", singularLabel: "Dokument", collectionPath: documents_path, newPath: new_document_path, updatePath: document_path },
  guide: { pluralLabel: "Guides", singularLabel: "Guide", collectionPath: guides_path, newPath: new_guide_path, updatePath: guide_path }
};
const DEFAULT_DOCUMENT_NAME = "Nyt dokument";

function timeAgo(createdAt) {
  if (!createdAt) return "";

  const elapsedSeconds = Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  if (elapsedSeconds < 60) return "lige nu";

  const units = [
    [31536000, "år", "år"],
    [2592000, "måned", "måneder"],
    [86400, "dag", "dage"],
    [3600, "time", "timer"],
    [60, "minut", "minutter"]
  ];
  const [secondsPerUnit, singular, plural] = units.find(([seconds]) => elapsedSeconds >= seconds);
  const value = Math.floor(elapsedSeconds / secondsPerUnit);

  return `for ${value} ${value === 1 ? singular : plural} siden`;
}

function VersionAssociations({ version, contentKind }) {
  const associations = contentKind === "template"
      ? (version.guide_versions || []).map((linkedVersion) => ({
        label: "Guide",
        name: linkedVersion.name,
        number: linkedVersion.number,
        href: linkedVersion.guide_id ? edit_guide_path(linkedVersion.guide_id) : null
      }))
      : contentKind === "guide"
      ? (version.template_versions || []).map((linkedVersion) => ({
        label: "Skabelon",
        name: linkedVersion.name,
        number: linkedVersion.number,
        href: linkedVersion.template_id ? edit_template_path(linkedVersion.template_id) : null
      }))
      : [];

  if (associations.length === 0) return null;

  return (
    <span className="content-library-history__associations">
      {associations.map((association) => (
        association.href ? (
          <a
            key={`${association.label}-${association.name}-${association.number}`}
            href={association.href}
            className="content-library-history__association-badge"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {association.label}: {association.name} · v{association.number}
          </a>
        ) : (
          <span key={`${association.label}-${association.name}-${association.number}`} className="content-library-history__association-badge">
            {association.label}: {association.name} · v{association.number}
          </span>
        )
      ))}
    </span>
  );
}

function lineDiffStats(previousVersion, version) {
  const changes = diffLines(previousVersion?.body || "", version.body || "");

  return changes.reduce((stats, change) => {
    if (change.added) stats.additions += change.count || 0;
    if (change.removed) stats.deletions += change.count || 0;

    return stats;
  }, { additions: 0, deletions: 0 });
}

function DiffStat({ stats }) {
  if (stats.additions === 0 && stats.deletions === 0) {
    return <span className="content-library-history__diff-stat is-unchanged">Ingen linjeændringer</span>;
  }

  return (
    <span className="content-library-history__diff-stat" aria-label={`${stats.additions} tilføjede og ${stats.deletions} fjernede linjer`}>
      {stats.additions > 0 ? <span className="is-added">+{stats.additions}</span> : null}
      {stats.deletions > 0 ? <span className="is-removed">−{stats.deletions}</span> : null}
      <span className="content-library-history__diff-stat-label">linjer</span>
    </span>
  );
}

const diffViewerStyles = {
  variables: {
    light: {
      emptyLineBackground: "#fff"
    }
  },
  diffAdded: { backgroundColor: "#e6ffed" },
  diffRemoved: { backgroundColor: "#ffeef0" }
};

function versionDiffs(versions) {
  const orderedVersions = [...versions].sort((left, right) => left.number - right.number);

  return new Map(orderedVersions.map((version, index) => {
    const previousVersion = orderedVersions[index - 1] || null;

    return [version.id, {
      previousVersion,
      stats: lineDiffStats(previousVersion, version)
    }];
  }));
}

function VersionHistory({ versions, selectedVersionId, onSelect = null, onShowDiff, contentKind }) {
  const diffByVersionId = useMemo(() => versionDiffs(versions), [versions]);

  return (
    <div className="content-library-history">
      {versions.length === 0 ? (
        <p className="workspace-empty__copy mb-0">Versioner vises her, når du gemmer.</p>
      ) : (
        versions.map((version) => {
          const diff = diffByVersionId.get(version.id);
          const previousVersion = diff?.previousVersion || null;
          const stats = diff?.stats || { additions: 0, deletions: 0 };

          return (
            <div
              key={version.id}
              className={`content-library-history__item ${selectedVersionId === version.id ? "is-selected" : ""}`}
              role={onSelect ? "button" : undefined}
              tabIndex={onSelect ? 0 : undefined}
              aria-pressed={onSelect ? selectedVersionId === version.id : undefined}
              onClick={onSelect ? (event) => {
                if (event.target.closest("button")) return;

                onSelect(version);
              } : undefined}
              onKeyDown={onSelect ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;

                event.preventDefault();
                onSelect(version);
              } : undefined}
            >
              <span className="content-library-history__topline">
                <strong>Version {version.number}</strong>
                <span className="content-library-history__meta">
                  <small>{timeAgo(version.created_at)}</small>
                </span>
              </span>

              <div className="content-library-history__footer">
                <DiffStat stats={stats} />
                <button
                  type="button"
                  className="content-library-history__diff-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onShowDiff(version, previousVersion);
                  }}
                >
                  Vis ændringer
                </button>
              </div>
              <VersionAssociations version={version} contentKind={contentKind} />
            </div>
          );
        })
      )}
    </div>
  );
}

function DocumentTemplateVersionHistory({ versions, activeVersionId, onShowDiff }) {
  return (
    <div className="content-library-template-version-history">
      {versions.map((templateVersion) => (
        <div
          key={templateVersion.id}
          className={`content-library-template-version-history__item ${activeVersionId === templateVersion.id ? "is-active" : ""}`}
        >
          <span className="content-library-template-version-history__references">
            <a
              href={edit_template_path(templateVersion.template_id)}
              className="content-library-template-version-history__link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>v{templateVersion.number}</strong>
            </a>
            {templateVersion.guide_version ? (
              <a
                href={guide_path(templateVersion.guide_version.guide_id)}
                className="content-library-history__association-badge"
                target="_blank"
                rel="noopener noreferrer"
              >
                {templateVersion.guide_version.name} · v{templateVersion.guide_version.number}
              </a>
            ) : null}
          </span>
          <div className="content-library-template-version-history__actions">
            {activeVersionId === templateVersion.id ? <span className="content-library-history__badge">Aktiv</span> : null}
            <button
              type="button"
              className="content-library-history__diff-button"
              onClick={() => onShowDiff(templateVersion, templateVersion.previous_version)}
            >
              Vis ændringer
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function VersionDiffDialog({ version, previousVersion, onClose, contentKind, updatePath, canEditVersionMetadata, onBeforeMetadataSave, onUpdated }) {
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [summary, setSummary] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setSummary(version?.change_summary || "");
    setReason(version?.change_reason || "");
    setError("");
    setIsEditingComment(false);
  }, [version?.id, version?.change_summary, version?.change_reason]);

  useEffect(() => {
    if (!version) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.classList.add("content-library-dialog-open");

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.classList.remove("content-library-dialog-open");
    };
  }, [version, onClose]);

  if (!version) return null;

  const stats = lineDiffStats(previousVersion, version);
  const cancelCommentEdit = () => {
    setSummary(version.change_summary || "");
    setReason(version.change_reason || "");
    setError("");
    setIsEditingComment(false);
  };

  const saveComment = () => {
    if (!summary.trim() || !reason.trim() || processing) return;

    setProcessing(true);
    setError("");
    onBeforeMetadataSave?.();
    router.patch(updatePath, {
      [contentKind]: {
        version_id: version.id,
        version_metadata: true,
        change_summary: summary.trim(),
        change_reason: reason.trim()
      }
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setIsEditingComment(false);
        onUpdated?.({ change_summary: summary.trim(), change_reason: reason.trim() });
      },
      onError: (errors) => {
        const messages = Object.values(errors || {}).flat().filter(Boolean);
        setError(messages.join(" ") || "Kommentaren kunne ikke opdateres.");
      },
      onFinish: () => setProcessing(false)
    });
  };

  return (
    <div className="content-library-change-dialog" role="presentation">
      <button type="button" className="content-library-change-dialog__backdrop" aria-label="Luk versionssammenligning" onClick={onClose} />
      <section className="content-library-change-dialog__panel content-library-change-dialog__panel--with-diff" role="dialog" aria-modal="true" aria-labelledby="content-library-diff-dialog-title">
        <div className="workspace-section__eyebrow">Versionssammenligning</div>
        <h2 id="content-library-diff-dialog-title" className="content-library-change-dialog__title">Version {version.number}</h2>
        <p className="content-library-change-dialog__copy">Se ændringerne mellem denne version og den tidligere version.</p>

        <div className="content-library-change-dialog__diff-heading">
          <div>
            <div className="workspace-section__eyebrow">Tekstændringer</div>
            <span>{previousVersion ? `Version ${previousVersion.number} sammenlignet med version ${version.number}` : "Første version"}</span>
          </div>
          <DiffStat stats={stats} />
        </div>

        <div className="content-library-change-dialog__diff">
          <ReactDiffViewer
            oldValue={previousVersion?.body || ""}
            newValue={version.body || ""}
            splitView
            compareMethod={DiffMethod.WORDS_WITH_SPACE}
            disableWorker
            showDiffOnly={false}
            hideSummary
            styles={diffViewerStyles}
            leftTitle={previousVersion ? `Version ${previousVersion.number}` : "Tom version"}
            rightTitle={`Version ${version.number}`}
          />
        </div>

        {isEditingComment ? (
          <div className="content-library-change-dialog__comment content-library-change-dialog__comment--editing">
              <label className="form-label fw-semibold" htmlFor="version-change-summary">Kort beskrivelse</label>
              <input id="version-change-summary" className="form-control" value={summary} onChange={(event) => setSummary(event.target.value)} required />
              <label className="form-label fw-semibold mt-3" htmlFor="version-change-reason">Hvorfor?</label>
              <textarea id="version-change-reason" className="form-control" rows="4" value={reason} onChange={(event) => setReason(event.target.value)} required />
              {error ? <div className="text-danger small mt-2" role="alert">{error}</div> : null}
          </div>
        ) : (
          <div className="content-library-change-dialog__comment">
            <div className="content-library-change-dialog__comment-heading-row">
              <div className="content-library-change-dialog__comment-heading">Kommentar</div>
              {canEditVersionMetadata ? (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setIsEditingComment(true)}>Redigér kommentar</button>
              ) : null}
            </div>
            <>
              <strong>{version.change_summary || "Ingen beskrivelse"}</strong>
              {version.change_reason ? <p>{version.change_reason}</p> : null}
              <small>Redigeret af {version.created_by || "ukendt bruger"} · {timeAgo(version.created_at)}</small>
            </>
          </div>
        )}

        <div className="content-library-change-dialog__actions">
          {isEditingComment ? (
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={cancelCommentEdit} disabled={processing}>Annuller</button>
              <button type="button" className="btn btn-primary" onClick={saveComment} disabled={processing || !summary.trim() || !reason.trim()}>{processing ? "Gemmer…" : "Gem kommentar"}</button>
            </>
          ) : (
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Luk</button>
          )}
        </div>
      </section>
    </div>
  );
}

function directGuideVersion(version) {
  return version?.guide_versions?.[0] || null;
}

function latestGuideVersion(versions, maximumNumber) {
  return [...versions]
    .filter((version) => version.number <= maximumNumber)
    .sort((left, right) => right.number - left.number)
    .map(directGuideVersion)
    .find(Boolean) || null;
}

function previousGuideVersion(versions, versionNumber) {
  return latestGuideVersion(versions, versionNumber - 1);
}

function nextGuideVersion(versions, versionNumber) {
  return [...versions]
    .filter((version) => version.number > versionNumber)
    .sort((left, right) => left.number - right.number)
    .map(directGuideVersion)
    .find(Boolean) || null;
}

function UnsavedChangesDialog({ isOpen, onSave, onLeave, onCancel }) {
  if (!isOpen) return null;

  return (
    <div className="content-library-unsaved-dialog" role="presentation">
      <button type="button" className="content-library-unsaved-dialog__backdrop" aria-label="Luk advarsel" onClick={onCancel} />
      <section className="content-library-unsaved-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="content-library-unsaved-dialog-title">
        <div className="workspace-section__eyebrow">Ikke gemt</div>
        <h2 id="content-library-unsaved-dialog-title" className="content-library-unsaved-dialog__title">Du har ændringer, der ikke er gemt</h2>
        <p className="content-library-unsaved-dialog__copy">Vil du gemme ændringerne, før du forlader siden?</p>

        <div className="content-library-unsaved-dialog__actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Bliv på siden</button>
          <button type="button" className="btn btn-outline-danger" onClick={onLeave}>Forlad uden at gemme</button>
          <button type="button" className="btn btn-primary" onClick={onSave}>Gem ændringer</button>
        </div>
      </section>
    </div>
  );
}

function RenameDialog({ item, contentKind, updatePath, onClose }) {
  const [name, setName] = useState(item?.name || "");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  const submit = (event) => {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Navnet må ikke være tomt.");
      return;
    }

    setProcessing(true);
    setError("");
    router.patch(updatePath(item.id), {
      [contentKind]: { name: trimmedName, rename: true }
    }, {
      preserveScroll: true,
      onSuccess: onClose,
      onError: (errors) => {
        const messages = Object.values(errors || {}).flat().filter(Boolean);
        setError(messages.join(" ") || "Navnet kunne ikke opdateres.");
      },
      onFinish: () => setProcessing(false)
    });
  };

  return (
    <div className="content-library-change-dialog" role="presentation">
      <button type="button" className="content-library-change-dialog__backdrop" aria-label="Luk omdøbning" onClick={onClose} />
      <section className="content-library-change-dialog__panel" role="dialog" aria-modal="true" aria-labelledby="content-library-rename-dialog-title">
        <div className="workspace-section__eyebrow">Omdøb</div>
        <h2 id="content-library-rename-dialog-title" className="content-library-change-dialog__title">Giv dette indhold et nyt navn</h2>
        <p className="content-library-change-dialog__copy">Navneændringen påvirker ikke versionshistorikken.</p>

        <form onSubmit={submit}>
          <label className="form-label fw-semibold" htmlFor="content-library-rename">Navn</label>
          <input
            id="content-library-rename"
            className="form-control"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError("");
            }}
            autoFocus
            required
          />
          {error ? <div className="text-danger small mt-2" role="alert">{error}</div> : null}

          <div className="content-library-change-dialog__actions">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={processing}>Annuller</button>
            <button type="submit" className="btn btn-primary" disabled={processing || !name.trim()}>{processing ? "Gemmer…" : "Gem navn"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function ChangeDialog({ isOpen, summary, reason, newBody, newVersionNumber, previousVersion, onSummaryChange, onReasonChange, onCancel, onConfirm, processing }) {
  if (!isOpen) return null;

  const stats = lineDiffStats(previousVersion, { body: newBody });

  return (
    <div className="content-library-change-dialog" role="presentation">
      <button type="button" className="content-library-change-dialog__backdrop" aria-label="Luk versionsdialog" onClick={onCancel} />
      <section className="content-library-change-dialog__panel content-library-change-dialog__panel--with-diff" role="dialog" aria-modal="true" aria-labelledby="content-library-change-dialog-title">
        <div className="workspace-section__eyebrow">Ny version</div>
        <h2 id="content-library-change-dialog-title" className="content-library-change-dialog__title">Hvad har ændret sig?</h2>
        <p className="content-library-change-dialog__copy">Beskriv både ændringen og hvorfor den blev lavet. Det bliver gemt i versionshistorikken.</p>

        <div className="content-library-change-dialog__diff-heading">
          <div>
            <div className="workspace-section__eyebrow">Tekstændringer</div>
            <span>{previousVersion ? `Version ${previousVersion.number} sammenlignet med ny version ${newVersionNumber}` : `Ny version ${newVersionNumber}`}</span>
          </div>
          <DiffStat stats={stats} />
        </div>

        <div className="content-library-change-dialog__diff">
          <ReactDiffViewer
            oldValue={previousVersion?.body || ""}
            newValue={newBody || ""}
            splitView
            compareMethod={DiffMethod.WORDS_WITH_SPACE}
            disableWorker
            showDiffOnly={false}
            hideSummary
            styles={diffViewerStyles}
            leftTitle={previousVersion ? `Version ${previousVersion.number}` : "Tom version"}
            rightTitle={`Ny version ${newVersionNumber}`}
          />
        </div>

        <label className="form-label fw-semibold" htmlFor="change-summary">Kort beskrivelse</label>
        <input id="change-summary" className="form-control" value={summary} onChange={(event) => onSummaryChange(event.target.value)} placeholder="Fx. Opdateret kontrolbeskrivelse" autoFocus required />

        <label className="form-label fw-semibold mt-3" htmlFor="change-reason">Hvorfor?</label>
        <textarea id="change-reason" className="form-control" rows="4" value={reason} onChange={(event) => onReasonChange(event.target.value)} placeholder="Fx. Kravene er ændret i den nye vejledning." required />

        <div className="content-library-change-dialog__actions">
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={processing}>Annuller</button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={processing || !summary.trim() || !reason.trim()}>{processing ? "Gemmer…" : "Gem version"}</button>
        </div>
      </section>
    </div>
  );
}

export default function ContentLibraryEdit({ content_kind: contentKind, content_label: contentLabel, item, is_new: isNew, errors = [], template_versions: templateVersions = [], guide_versions: guideVersions = [], guides = [], pdf_preview_url: pdfPreviewUrl, template_review: templateReview = null, template_version_history: templateVersionHistory = [], document_guide: documentGuide = null, can_edit: canEdit, can_edit_version_metadata: canEditVersionMetadata = false }) {
  const config = configs[contentKind] || configs.document;
  const hasInlineEditableName = contentKind === "document" || contentKind === "template";
  const defaultContentName = contentKind === "document" ? DEFAULT_DOCUMENT_NAME : `Ny ${contentLabel.toLowerCase()}`;
  const setHeaderActions = useAuthLayoutActions();
  const setHeaderTitle = useAuthLayoutTitle();
  const currentVersion = item.versions.find((version) => version.id === item.current_version_id) || item.versions[0];
  const initialVersion = currentVersion;
  const latestVersion = [...item.versions].sort((left, right) => right.number - left.number)[0] || null;
  const newVersionNumber = (latestVersion?.number || 0) + 1;
  const linkedTemplateVersion = initialVersion?.template_versions?.[0]?.id || "";
  const lockedGuideId = contentKind === "template" ? item.versions
    .flatMap((version) => version.guide_versions || [])
    .map((version) => version.guide_id)
    .find(Boolean) || "" : "";
  const linkedGuideVersionId = initialVersion?.guide_versions?.[0]?.id || "";
  const isFileDocument = contentKind === "document" && !isNew && item.is_text_document === false;
  const isEditable = canEdit && !isFileDocument;
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(initialVersion?.id || null);
  const [viewedVersionId, setViewedVersionId] = useState(initialVersion?.id || null);
  const [templateVersionId, setTemplateVersionId] = useState(linkedTemplateVersion);
  const [savedTemplateVersionId, setSavedTemplateVersionId] = useState(linkedTemplateVersion);
  const [guideId, setGuideId] = useState(lockedGuideId);
  const [guideVersionId, setGuideVersionId] = useState(linkedGuideVersionId);
  const [savedGuideVersionId, setSavedGuideVersionId] = useState(linkedGuideVersionId);
  const [isAssociationSaving, setIsAssociationSaving] = useState(false);
  const [contentName, setContentName] = useState(item.name || defaultContentName);
  const [isContentNameSaving, setIsContentNameSaving] = useState(false);
  const [isUnsavedDialogOpen, setIsUnsavedDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isTemplateReviewDiffOpen, setIsTemplateReviewDiffOpen] = useState(false);
  const [isTemplateReviewSaving, setIsTemplateReviewSaving] = useState(false);
  const [reviewedTemplateVersionId, setReviewedTemplateVersionId] = useState(templateReview?.reviewed_version_id || null);
  const [diffSelection, setDiffSelection] = useState(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState("editor");
  const [changeSummary, setChangeSummary] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const pendingVersionRef = useRef(null);
  const pendingVisitRef = useRef(null);
  const bypassNavigationGuardRef = useRef(false);
  const selectedVersion = item.versions.find((version) => version.id === selectedVersionId) || currentVersion;
  const viewedVersion = item.versions.find((version) => version.id === viewedVersionId) || currentVersion;
  const needsTemplateReview = contentKind === "document" &&
    templateReview?.needs_review &&
    reviewedTemplateVersionId !== templateReview.latest_version?.id;
  const activeTemplateVersionId = templateReview?.used_version?.id ||
    currentVersion?.template_versions?.[0]?.id || null;
  const effectiveGuideVersion = contentKind === "template" && selectedVersion
    ? latestGuideVersion(item.versions, selectedVersion.number)
    : null;
  const previousGuide = contentKind === "template" && selectedVersion
    ? previousGuideVersion(item.versions, selectedVersion.number)
    : null;
  const nextGuide = contentKind === "template" && selectedVersion
    ? nextGuideVersion(item.versions, selectedVersion.number)
    : null;
  const selectedGuideVersions = contentKind === "template" && guideId
    ? guideVersions.filter((version) => version.guide_id === guideId)
    : [];
  const isPrimaryGuideLocked = contentKind === "template" && Boolean(lockedGuideId);
  const availableGuides = guides.filter((guide) => !isPrimaryGuideLocked || guide.id === lockedGuideId);
  const enforceGuideSequence = contentKind === "template" && Boolean(guideId);
  const availableGuideVersions = selectedGuideVersions.filter((version) => (
    !enforceGuideSequence || (
    (
      (!previousGuide || version.number > previousGuide.number) &&
      (!nextGuide || version.number < nextGuide.number)
    ) || version.id === guideVersionId
    )
  ));
  const { data, setData, post, put, transform, isDirty, processing } = useForm({
    body: normalizeRichText(initialVersion?.body || item.body || ""),
    change_summary: "",
    change_reason: ""
  });
  const previewVersionNumber = isNew || isDirty ? newVersionNumber : currentVersion?.number;
  const previewVersionId = !isNew && !isDirty ? currentVersion?.id : null;

  const workspaceTabs = [
    ["editor", "Redigér", "bi-pencil-square"],
    ["preview", "Forhåndsvisning", "bi-eye"],
    ...(contentKind === "document" && documentGuide ? [["guide", "Guide", "bi-journal-text"]] : []),
    ["versions", "Historik", "bi-clock-history"]
  ];

  const isAssociationDirty = contentKind === "template"
    ? guideVersionId !== savedGuideVersionId
    : templateVersionId !== savedTemplateVersionId;
  const isNewContentNameDirty = hasInlineEditableName && isNew && contentName !== defaultContentName;
  const hasUnsavedChanges = isDirty || isAssociationDirty || isNewContentNameDirty;
  const isDirtyRef = useRef(hasUnsavedChanges);
  isDirtyRef.current = hasUnsavedChanges;

  useEffect(() => {
    setSelectedVersionId(currentVersion?.id || null);
    setViewedVersionId(currentVersion?.id || null);
    setTemplateVersionId(linkedTemplateVersion);
    setSavedTemplateVersionId(linkedTemplateVersion);
    setGuideId(lockedGuideId);
    setGuideVersionId(linkedGuideVersionId);
    setSavedGuideVersionId(linkedGuideVersionId);
  }, [item.current_version_id, linkedTemplateVersion, lockedGuideId, linkedGuideVersionId]);

  useEffect(() => {
    setReviewedTemplateVersionId(templateReview?.reviewed_version_id || null);
  }, [item.id, templateReview?.reviewed_version_id, templateReview?.latest_version?.id]);

  useEffect(() => {
    setContentName(item.name || defaultContentName);
  }, [contentKind, defaultContentName, isNew, item.id, item.name]);

  useEffect(() => {
    if (activeWorkspaceTab === "guide" && !documentGuide) setActiveWorkspaceTab("editor");
  }, [activeWorkspaceTab, documentGuide?.id]);

  useEffect(() => {
    const removeBeforeListener = router.on("before", (event) => {
      if (bypassNavigationGuardRef.current) {
        bypassNavigationGuardRef.current = false;
        return;
      }

      if (!isDirtyRef.current) return;

      event.preventDefault();
      const visit = event.detail.visit;
      pendingVisitRef.current = {
        url: visit.url,
        options: {
          method: visit.method,
          data: visit.data,
          replace: visit.replace,
          preserveScroll: visit.preserveScroll,
          preserveState: visit.preserveState,
          preserveUrl: visit.preserveUrl,
          only: visit.only,
          except: visit.except,
          reset: visit.reset,
          headers: visit.headers,
          errorBag: visit.errorBag,
          forceFormData: visit.forceFormData,
          queryStringArrayFormat: visit.queryStringArrayFormat,
          showProgress: visit.showProgress,
          async: visit.async
        }
      };
      setIsUnsavedDialogOpen(true);
    });

    const handleBeforeUnload = (event) => {
      if (!isDirtyRef.current) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      removeBeforeListener();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const openSaveDialog = () => {
    if (!isEditable || !hasUnsavedChanges) return;

    if (!isNew && !isDirty && isAssociationDirty) {
      saveAssociation();
      return;
    }

    setIsChangeDialogOpen(true);
  };

  const submit = (event) => {
    event.preventDefault();
    openSaveDialog();
  };

  const applyAssociationVersion = (version) => {
    const directGuideVersion = version.guide_versions?.[0] || null;
    const nextGuideVersionId = directGuideVersion?.id || "";

    setSelectedVersionId(version.id);
    setGuideId((currentGuideId) => lockedGuideId || directGuideVersion?.guide_id || currentGuideId);
    setGuideVersionId(nextGuideVersionId);
    setSavedGuideVersionId(nextGuideVersionId);
  };

  const applyVersionSelection = (version) => {
    setViewedVersionId(version.id);

    if (contentKind === "template") applyAssociationVersion(version);
  };

  const selectVersion = (version) => {
    if (!version || (version.id === viewedVersionId && (contentKind !== "template" || version.id === selectedVersionId))) return;

    if (contentKind === "template" && version.id !== selectedVersionId && isDirtyRef.current) {
      pendingVersionRef.current = version;
      setIsUnsavedDialogOpen(true);
      return;
    }

    applyVersionSelection(version);
  };

  const continuePendingAction = () => {
    const pendingVersion = pendingVersionRef.current;
    const pendingVisit = pendingVisitRef.current;

    pendingVersionRef.current = null;
    pendingVisitRef.current = null;

    if (pendingVersion) {
      applyVersionSelection(pendingVersion);
      return;
    }

    if (pendingVisit) {
      bypassNavigationGuardRef.current = true;
      router.visit(pendingVisit.url, pendingVisit.options);
    }
  };

  const saveAssociation = ({ continueAfterSave = false } = {}) => {
    if (isNew || !selectedVersionId || isAssociationSaving) return;

    setIsAssociationSaving(true);
    bypassNavigationGuardRef.current = true;
    const associationData = contentKind === "template"
      ? { version_id: selectedVersionId, guide_version_id: guideVersionId }
      : { version_id: selectedVersionId, template_version_id: templateVersionId };

    router.patch(config.updatePath(item.id), {
      [contentKind]: associationData
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        if (contentKind === "template") {
          setSavedGuideVersionId(guideVersionId);
        } else {
          setSavedTemplateVersionId(templateVersionId);
        }
        if (continueAfterSave) continuePendingAction();
      },
      onError: () => {
        if (contentKind === "template") {
          setGuideVersionId(savedGuideVersionId);
        } else {
          setTemplateVersionId(savedTemplateVersionId);
        }
      },
      onFinish: () => setIsAssociationSaving(false)
    });
  };

  const confirmTemplateReview = () => {
    if (!templateReview?.latest_version || isTemplateReviewSaving || hasUnsavedChanges) return;

    setIsTemplateReviewSaving(true);
    bypassNavigationGuardRef.current = true;
    router.patch(config.updatePath(item.id), {
      document: { template_review: true }
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => setReviewedTemplateVersionId(templateReview.latest_version.id),
      onFinish: () => setIsTemplateReviewSaving(false)
    });
  };

  useEffect(() => {
    if (!hasInlineEditableName) return undefined;

    setHeaderTitle({
      value: contentName,
      placeholder: defaultContentName,
      editable: canEdit,
      processing: isContentNameSaving,
      ariaLabel: `Redigér ${contentLabel.toLowerCase()}navn`,
      onCommit: (nextName) => {
        const trimmedName = nextName.trim();
        if (!trimmedName || trimmedName === contentName) return;

        setContentName(trimmedName);
        if (isNew) return;

        setIsContentNameSaving(true);
        bypassNavigationGuardRef.current = true;
        router.patch(config.updatePath(item.id), {
          [contentKind]: { name: trimmedName, rename: true }
        }, {
          preserveScroll: true,
          preserveState: true,
          onError: () => setContentName(item.name || defaultContentName),
          onFinish: () => setIsContentNameSaving(false)
        });
      }
    });

    return () => setHeaderTitle(null);
  }, [
    canEdit,
    contentLabel,
    contentKind,
    contentName,
    defaultContentName,
    hasInlineEditableName,
    isContentNameSaving,
    isNew,
    item.id,
    item.name,
    setHeaderTitle
  ]);

  useEffect(() => {
    const canRename = canEdit && !isNew && !hasInlineEditableName;

    if (!isEditable) {
      setHeaderActions(canRename ? [{
        label: "Omdøb",
        onClick: () => setIsRenameDialogOpen(true),
        variant: "outline-secondary"
      }] : []);
      return () => setHeaderActions([]);
    }

    setHeaderActions([
      ...(canRename ? [{
        label: "Omdøb",
        onClick: () => setIsRenameDialogOpen(true),
        variant: "outline-secondary"
      }] : []),
      {
        label: "Gem",
        onClick: openSaveDialog,
        variant: hasUnsavedChanges ? "primary" : "outline-secondary",
        disabled: !hasUnsavedChanges || processing || isAssociationSaving || isContentNameSaving,
        ariaLabel: hasUnsavedChanges ? "Gem ændringer" : "Ingen ændringer at gemme"
      }
    ]);

    return () => setHeaderActions([]);
  // Header actions live in the layout context, so keep their save callback in
  // sync with every association field rather than only the dirty boolean.
  }, [
    canEdit,
    isEditable,
    isNew,
    hasUnsavedChanges,
    isDirty,
    isAssociationDirty,
    selectedVersionId,
    guideId,
    guideVersionId,
    savedGuideVersionId,
    templateVersionId,
    savedTemplateVersionId,
    processing,
    isAssociationSaving,
    isContentNameSaving,
    hasInlineEditableName,
    pdfPreviewUrl,
    setHeaderActions
  ]);

  const leaveWithoutSaving = () => {
    const pendingVersion = pendingVersionRef.current;
    const pendingVisit = pendingVisitRef.current;

    pendingVersionRef.current = null;
    pendingVisitRef.current = null;
    setIsUnsavedDialogOpen(false);

    if (pendingVersion) {
      applyVersionSelection(pendingVersion);
      return;
    }

    if (pendingVisit) {
      bypassNavigationGuardRef.current = true;
      router.visit(pendingVisit.url, pendingVisit.options);
    }
  };

  const saveBeforeLeaving = () => {
    setIsUnsavedDialogOpen(false);

    if (!isNew && !isDirty && isAssociationDirty) {
      saveAssociation({ continueAfterSave: true });
      return;
    }

    setIsChangeDialogOpen(true);
  };

  const confirmSave = () => {
    setIsChangeDialogOpen(false);
    bypassNavigationGuardRef.current = true;
    const associationData = contentKind === "template"
      ? { guide_version_id: guideVersionId }
      : { template_version_id: templateVersionId };

    transform((formData) => ({
      [contentKind]: {
        ...formData,
        ...associationData,
        ...(hasInlineEditableName ? { name: contentName.trim() || defaultContentName } : {}),
        change_summary: changeSummary,
        change_reason: changeReason
      }
    }));

    if (isNew) {
      post(config.collectionPath());
    } else {
      put(config.updatePath(item.id), {
        onSuccess: continuePendingAction
      });
    }
  };

  const documentTemplateVersionsPanel = contentKind === "document" ? (
    <section className={`content-library-workspace__sidebar-card content-library-template-version-card ${needsTemplateReview ? "has-review" : ""}`}>
      <div className="content-library-workspace__sidebar-heading">
        <div className="workspace-section__eyebrow">Sammenhæng</div>
        <h2>Skabelonversioner</h2>
        <p>Se de skabelonversioner, som dokumentet bygger på.</p>
      </div>

      {needsTemplateReview ? (
        <div className="content-library-template-version-card__review">
          <p className="workspace-card-description">
            Dokumentet tager udgangspunkt i v{templateReview.used_version.number}, men der er en nyere skabelonversion v{templateReview.latest_version.number}. Bekræft ændringerne, før du gemmer dokumentet.
          </p>
          <div className="content-library-template-review-card__versions">
            <span className="workspace-chip">v{templateReview.used_version.number}</span>
            <span className="content-library-template-review-card__arrow">→</span>
            <span className="workspace-chip">v{templateReview.latest_version.number}</span>
          </div>
          <div className="content-library-template-review-card__actions">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setIsTemplateReviewDiffOpen(true)}>
              Se ændringer
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={confirmTemplateReview} disabled={isTemplateReviewSaving || processing || hasUnsavedChanges}>
              {isTemplateReviewSaving ? "Gemmer…" : "Bekræft ændringer"}
            </button>
          </div>
          {hasUnsavedChanges ? <small className="content-library-template-review-card__hint">Gem dine ændringer først.</small> : null}
        </div>
      ) : null}

      {templateVersionHistory.length > 0 ? (
        <DocumentTemplateVersionHistory
          versions={templateVersionHistory}
          activeVersionId={activeTemplateVersionId}
          onShowDiff={(version, previousVersion) => setDiffSelection({
            version,
            previousVersion,
            contentKind: "template",
            updatePath: template_path(version.template_id)
          })}
        />
      ) : (
        <p className="content-library-workspace__empty-sidebar">Dette dokument har endnu ikke en tilknyttet skabelonversion.</p>
      )}
    </section>
  ) : null;

  const documentGuidePanel = contentKind === "document" && documentGuide ? (
    <section className="content-library-workspace__sidebar-card">
      <div className="content-library-workspace__sidebar-heading">
        <div className="workspace-section__eyebrow">Guide</div>
        <h2>{documentGuide.name}</h2>
        <p>Guiden forklarer, hvordan skabelonversion v{documentGuide.template_version_number} udfyldes.</p>
      </div>
      <span className="workspace-chip">Guide v{documentGuide.number}</span>
    </section>
  ) : null;

  const guideTemplatePanel = contentKind === "guide" && templateVersions.length > 0 ? (
    <section className="content-library-workspace__sidebar-card">
      <div className="content-library-workspace__sidebar-heading">
        <div className="workspace-section__eyebrow">Sammenhæng</div>
        <h2>Skabelonversion</h2>
        <p>Vælg den skabelonversion, som denne version er baseret på.</p>
      </div>
      <select className="form-select" value={templateVersionId} onChange={(event) => setTemplateVersionId(event.target.value)} disabled={isAssociationSaving || processing}>
        <option value="">Ingen skabelon valgt</option>
        {templateVersions.map((version) => <option key={version.id} value={version.id}>{version.label}</option>)}
      </select>
    </section>
  ) : null;

  const templateGuidePanel = contentKind === "template" && (guides.length > 0 || guideVersions.length > 0 || effectiveGuideVersion) ? (
    <section className="content-library-workspace__sidebar-card">
      <div className="content-library-workspace__sidebar-heading">
        <div className="workspace-section__eyebrow">Sammenhæng</div>
        <h2>Guide til version v{selectedVersion?.number || newVersionNumber}</h2>
        <p>Vælg den guide og guideversion, som gælder for netop denne skabelonversion.</p>
      </div>
      {guides.length > 0 ? (
        <select
          className="form-select content-library-workspace__primary-guide-select"
          value={guideId}
          onChange={(event) => {
            const nextGuideId = event.target.value;
            setGuideId(nextGuideId);
            if (!guideVersions.some((version) => version.id === guideVersionId && version.guide_id === nextGuideId)) {
              setGuideVersionId("");
            }
          }}
          disabled={isAssociationSaving || processing || isPrimaryGuideLocked}
        >
          <option value="">Ingen guide valgt</option>
          {availableGuides.map((guide) => <option key={guide.id} value={guide.id}>{guide.name}</option>)}
        </select>
      ) : null}

      {isPrimaryGuideLocked ? (
        <p className="content-library-workspace__primary-guide-hint">Guiden er låst, fordi mindst én skabelonversion allerede er knyttet til en guideversion.</p>
      ) : null}

      {guideId ? (
        <div className="content-library-workspace__field-group">
          <div className="workspace-section__eyebrow">Guideversion</div>
          <p className="workspace-card-description">Vælg guideversionen til skabelonversion v{selectedVersion?.number || newVersionNumber}.</p>
          <select className="form-select" value={guideVersionId} onChange={(event) => setGuideVersionId(event.target.value)} disabled={isAssociationSaving || processing}>
            <option value="">
              {effectiveGuideVersion
                ? `Ingen direkte guideversion (bruger ${effectiveGuideVersion.name} · v${effectiveGuideVersion.number})`
                : "Ingen guideversion valgt"}
            </option>
            {availableGuideVersions.map((version) => <option key={version.id} value={version.id}>{version.label}</option>)}
          </select>
        </div>
      ) : null}

      <div className="content-library-workspace__effective-guide">
        <div className="workspace-section__eyebrow">Effektiv guide</div>
        <p className="workspace-card-description">Den seneste guide fra denne eller en tidligere skabelonversion.</p>
        {effectiveGuideVersion ? (
          <span className="workspace-chip">{effectiveGuideVersion.name} · v{effectiveGuideVersion.number}</span>
        ) : (
          <span className="text-secondary">Ingen guide valgt.</span>
        )}
      </div>
    </section>
  ) : null;

  const contextSidebar = documentTemplateVersionsPanel || guideTemplatePanel || templateGuidePanel || (
    <section className="content-library-workspace__sidebar-card">
      <div className="content-library-workspace__sidebar-heading">
        <div className="workspace-section__eyebrow">Sammenhæng</div>
        <h2>Ingen sammenhæng endnu</h2>
        <p>Der er ingen versioner eller relationer at vise for dette indhold endnu.</p>
      </div>
    </section>
  );

  return (
    <>
      <Head title={hasInlineEditableName ? contentName : (isNew ? `Ny ${contentLabel}` : item.name)} />

      <section className="workspace-content content-library-editor-page">
        {errors.length > 0 ? (
          <div className="alert alert-danger" role="alert">{errors.join(" ")}</div>
        ) : null}

        {isFileDocument ? (
          <div className="alert alert-info">Dette er et uploadet fil-dokument. Versionseditoren er kun til Markdown-dokumenter.</div>
        ) : (
          <form onSubmit={submit} className="content-library-workspace">
            <div className="content-library-workspace__controls">
              <nav className="content-library-workspace__tabs" role="tablist" aria-label="Dokumentarbejdsområde">
                {workspaceTabs.map(([tab, label, icon]) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={activeWorkspaceTab === tab}
                    className={`content-library-workspace__tab ${activeWorkspaceTab === tab ? "is-active" : ""}`}
                    onClick={() => setActiveWorkspaceTab(tab)}
                  >
                    <i className={`bi ${icon}`} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </nav>

              <div className="content-library-workspace__toolbar-row">
                <div id="content-library-editor-toolbar" className="content-library-workspace__toolbar" aria-label="Redigeringsværktøjer" />
              </div>
            </div>

            <div className="content-library-workspace__content">
              <div className="content-library-editor-layout content-library-editor-layout--workspace" hidden={activeWorkspaceTab !== "editor"}>
                <div className="content-library-editor-layout__body">
                  <div className="content-library-editor-main">
                    <div className="content-library-editor-surface">
                      <RichTextEditor
                        value={data.body}
                        onChange={(value) => setData("body", value)}
                        disabled={!isEditable || activeWorkspaceTab !== "editor"}
                        toolbarContainerId="content-library-editor-toolbar"
                      />
                    </div>
                  </div>

                  <aside className="content-library-editor-sidebar">
                    {contextSidebar}
                  </aside>
                </div>
              </div>

              {activeWorkspaceTab === "versions" ? (
                <section className="content-library-workspace__versions-layout">
                  <div className="content-library-workspace__version-viewer">
                    <section className="content-library-workspace__preview content-library-workspace__version-preview">
                      {pdfPreviewUrl ? (
                        <ContentLibraryPdfPreview
                          key={viewedVersion?.id || "empty-version"}
                          previewUrl={pdfPreviewUrl}
                          body={viewedVersion?.body || ""}
                          versionNumber={viewedVersion?.number}
                          recordId={item.id}
                          versionId={viewedVersion?.id}
                          title={`${contentLabel}: ${hasInlineEditableName ? contentName : (item.name || "Version")} · v${viewedVersion?.number || ""}`}
                          className="content-library-workspace__preview-pdf"
                          enabled
                          hideNativeToolbar
                        />
                      ) : (
                        <div className="content-library-workspace__preview-empty">
                          <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                          <p>PDF-previewet er ikke tilgængeligt endnu.</p>
                        </div>
                      )}
                    </section>
                  </div>

                  <aside className="content-library-workspace__versions-sidebar">
                    <section className="content-library-workspace__sidebar-card">
                      <div className="content-library-workspace__section-heading">
                        <div>
                          <div className="workspace-section__eyebrow">Historik</div>
                          <h2>Versioner</h2>
                        </div>
                        <span className="content-library-workspace__count">{item.versions.length}</span>
                      </div>
                      <VersionHistory
                        versions={item.versions}
                        selectedVersionId={viewedVersion?.id}
                        onSelect={selectVersion}
                        onShowDiff={(version, previousVersion) => setDiffSelection({ version, previousVersion })}
                        contentKind={contentKind}
                      />
                    </section>

                    {contentKind === "template" && templateGuidePanel ? (
                      <div className="content-library-workspace__version-association">
                        {templateGuidePanel}
                      </div>
                    ) : null}
                  </aside>
                </section>
              ) : null}

              {activeWorkspaceTab === "preview" ? (
                <div className="content-library-editor-layout__body content-library-workspace__preview-layout">
                  <div className="content-library-editor-main">
                    <section className="content-library-workspace__preview">
                      {pdfPreviewUrl ? (
                        <ContentLibraryPdfPreview
                          previewUrl={pdfPreviewUrl}
                          body={data.body}
                          versionNumber={previewVersionNumber}
                          recordId={isNew ? null : item.id}
                          versionId={previewVersionId}
                          title={`${contentLabel}: ${hasInlineEditableName ? contentName : (item.name || "PDF-preview")}`}
                          className="content-library-workspace__preview-pdf"
                          enabled
                          hideNativeToolbar
                        />
                      ) : (
                        <div className="content-library-workspace__preview-empty">
                          <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                          <p>PDF-previewet er ikke tilgængeligt endnu.</p>
                        </div>
                      )}
                    </section>
                  </div>

                  <aside className="content-library-editor-sidebar">
                    {contextSidebar}
                  </aside>
                </div>
              ) : null}

              {activeWorkspaceTab === "guide" && documentGuide ? (
                <div className="content-library-editor-layout__body content-library-workspace__preview-layout">
                  <div className="content-library-editor-main">
                    <section className="content-library-workspace__preview">
                      {pdfPreviewUrl ? (
                        <ContentLibraryPdfPreview
                          key={documentGuide.id}
                          previewUrl={pdfPreviewUrl}
                          body={documentGuide.body}
                          versionNumber={documentGuide.number}
                          title={`Guide: ${documentGuide.name} · v${documentGuide.number}`}
                          className="content-library-workspace__preview-pdf"
                          enabled
                          hideNativeToolbar
                        />
                      ) : (
                        <div className="content-library-workspace__preview-empty">
                          <i className="bi bi-file-earmark-pdf" aria-hidden="true" />
                          <p>Guide-previewet er ikke tilgængeligt endnu.</p>
                        </div>
                      )}
                    </section>
                  </div>

                  <aside className="content-library-editor-sidebar">
                    {documentGuidePanel}
                  </aside>
                </div>
              ) : null}
            </div>
          </form>
        )}
      </section>

      <ChangeDialog
        isOpen={isChangeDialogOpen}
        summary={changeSummary}
        reason={changeReason}
        newBody={data.body}
        newVersionNumber={newVersionNumber}
        previousVersion={latestVersion}
        onSummaryChange={(value) => {
          setChangeSummary(value);
        }}
        onReasonChange={(value) => {
          setChangeReason(value);
        }}
        onCancel={() => setIsChangeDialogOpen(false)}
        onConfirm={confirmSave}
        processing={processing}
      />

      {isRenameDialogOpen ? (
        <RenameDialog
          item={item}
          contentKind={contentKind}
          updatePath={config.updatePath}
          onClose={() => setIsRenameDialogOpen(false)}
        />
      ) : null}

      <UnsavedChangesDialog
        isOpen={isUnsavedDialogOpen}
        onSave={saveBeforeLeaving}
        onLeave={leaveWithoutSaving}
        onCancel={() => {
          pendingVersionRef.current = null;
          pendingVisitRef.current = null;
          setIsUnsavedDialogOpen(false);
        }}
      />

      <VersionDiffDialog
        version={diffSelection?.version}
        previousVersion={diffSelection?.previousVersion}
        onClose={() => setDiffSelection(null)}
        contentKind={diffSelection?.contentKind || contentKind}
        updatePath={diffSelection?.updatePath || (item.id ? config.updatePath(item.id) : undefined)}
        canEditVersionMetadata={canEditVersionMetadata}
        onBeforeMetadataSave={() => {
          bypassNavigationGuardRef.current = true;
        }}
        onUpdated={(changes) => {
          setDiffSelection((selection) => selection
            ? { ...selection, version: { ...selection.version, ...changes } }
            : selection);
        }}
      />

      <VersionDiffDialog
        version={isTemplateReviewDiffOpen ? templateReview?.latest_version : null}
        previousVersion={isTemplateReviewDiffOpen ? templateReview?.used_version : null}
        onClose={() => setIsTemplateReviewDiffOpen(false)}
        contentKind="template"
        canEditVersionMetadata={false}
      />
    </>
  );
}

ContentLibraryEdit.layout = (page) => {
  const { layout_header: layoutHeader } = page.props;

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: layoutHeader?.breadcrumb || "Bibliotek" },
          { label: layoutHeader?.title || "Redigér" }
        ],
        title: layoutHeader?.title || "Redigér"
      }}
    >
      {page}
    </AuthLayout>
  );
};
